import { useEffect, useMemo, useState } from 'react';

import type { AssetsCollectionElasticSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';

import {
  attachChainBranches,
  fetchChainChildren,
  selectRevealChain,
} from '../../ducks/collection_fetch/thunks';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';

export const useRevealCollection = (
  collectionId: string | undefined,
  enabled: boolean,
) => {
  const dispatch = useAppDispatch();

  // Selected as a string so the result is stable, then split back: an array
  // would be a new reference on every action and re-render the whole panel.
  const chainKey = useAppSelector((state) =>
    collectionId ? selectRevealChain(state, collectionId).join(',') : '',
  );
  const chain = useMemo(
    () => (chainKey ? chainKey.split(',') : []),
    [chainKey],
  );

  const [childrenByParent, setChildrenByParent] =
    useState<Map<string, AssetsCollectionElasticSchemaWithIdNullable[]>>();

  useEffect(() => {
    setChildrenByParent(undefined);
    if (!enabled || !chain.length) {
      return;
    }

    dispatch(fetchChainChildren(chain)).then(setChildrenByParent);
  }, [enabled, chain]);

  const hasChainRoot = useAppSelector(
    (state) =>
      chain.length > 0 &&
      state.collectionsTree.flatTree.some((node) => node.model.id === chain[0]),
  );

  useEffect(() => {
    if (!enabled || !hasChainRoot) {
      return;
    }
    dispatch(attachChainBranches(chain, childrenByParent));
  }, [enabled, chain, childrenByParent, hasChainRoot]);
};
